import React from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../../common/auth";
import { AdminUserRow, updateTariffRate, updateWhatsappNumber } from "../../../common/requests/users/endpoints";
import { errorMessage, useIsNarrow, useToast } from "../../../ui";
import { normalizeWhatsapp, parseTariffPercent, sameTariff, tariffDraft } from "../userData";

interface SellerSettingsDialogProps {
  open: boolean;
  user: AdminUserRow | null;
  /** Which field gets focus when the dialog opens */
  focus?: "tariff" | "whatsapp";
  onClose: () => void;
  /** Patch the row after a save */
  onSaved: (userId: string, updates: Partial<AdminUserRow>) => void;
  /** Reload everything (a partial save may have gone through) */
  onReload: () => void;
}

/** Edit a seller's tariff (fee Inflos takes per sale) and WhatsApp alert number. */
export default function SellerSettingsDialog({ open, user, focus = "tariff", onClose, onSaved, onReload }: SellerSettingsDialogProps) {
  const { idToken } = useAuth();
  const toast = useToast();
  const narrow = useIsNarrow(600);
  const [tariff, setTariff] = React.useState("");
  const [whatsapp, setWhatsapp] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !user) return;
    setTariff(tariffDraft(user.tariff_rate));
    setWhatsapp(user.whatsapp_number || "");
    setError(null);
  }, [open, user]);

  const save = async () => {
    if (!idToken || !user?.user_id) return;
    const userId = user.user_id;
    const parsed = parseTariffPercent(tariff);
    if ("error" in parsed) {
      setError(parsed.error);
      return;
    }
    const tariffChanged = !sameTariff(user.tariff_rate, parsed.rate);
    const nextWhatsapp = normalizeWhatsapp(whatsapp);
    const whatsappChanged = nextWhatsapp !== (user.whatsapp_number || "");
    if (!tariffChanged && !whatsappChanged) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updates: Partial<AdminUserRow> = {};
      if (tariffChanged) {
        const res = await updateTariffRate(userId, parsed.rate, idToken);
        updates.tariff_rate = Number(res?.data?.tariff_rate ?? parsed.rate);
      }
      if (whatsappChanged) {
        const res = await updateWhatsappNumber(userId, nextWhatsapp || null, idToken);
        updates.whatsapp_number = res?.data?.whatsapp_number ?? null;
      }
      onSaved(userId, updates);
      toast("Seller settings saved");
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Couldn't save seller settings."));
      // A tariff save may have gone through before the WhatsApp save failed.
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen={narrow} fullWidth maxWidth="xs">
      <DialogTitle>Seller settings</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField
          label="Seller tariff (%)"
          type="number"
          fullWidth
          autoFocus={focus === "tariff"}
          value={tariff}
          onChange={(e) => setTariff(e.target.value)}
          inputProps={{ step: "0.01", min: 0, max: 100, inputMode: "decimal" }}
          helperText="Fee Inflos charges on each sale"
        />
        <TextField
          label="WhatsApp number"
          fullWidth
          autoFocus={focus === "whatsapp"}
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          placeholder="+6591234567"
          inputProps={{ inputMode: "tel" }}
          helperText="New-order alerts go here. Include the country code; leave blank to turn them off."
          InputProps={{
            endAdornment: whatsapp ? (
              <InputAdornment position="end">
                <Tooltip title="Remove number">
                  <IconButton onClick={() => setWhatsapp("")} edge="end" aria-label="Remove number" sx={{ width: 44, height: 44 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : undefined,
          }}
        />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1, "& > *": { flex: { xs: "1 1 auto", sm: "0 0 auto" } } }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
